import { Checkbox, NumberInput, TextInput } from "@tredespace/ui/widgets";
import type { JSX } from "react";
import type { BuildPlane } from "../../engine/planes.ts";
import { MIN_PLANE_SIZE, updatePlane } from "../../state/planes/planes.actions.ts";
import { Field } from "./Field.tsx";

type PlaneFieldsProps = Readonly<{ plane: BuildPlane }>;

/** Position, size and look of one build plane — every field writes straight through. */
export function PlaneFields({ plane }: PlaneFieldsProps): JSX.Element {
  const set = (patch: Partial<Omit<BuildPlane, "id">>): void => updatePlane(plane.id, patch);

  return (
    <div className="flex flex-col gap-2">
      <TextInput value={plane.name} onChange={(name) => set({ name })} placeholder="Name" clearable={false} />

      <div className="grid grid-cols-3 gap-2">
        <Field label="X m">
          <NumberInput value={plane.x} onChange={(x) => set({ x })} step={0.1} precision={3} />
        </Field>
        <Field label="Height m">
          <NumberInput value={plane.y} onChange={(y) => set({ y })} step={0.1} precision={3} />
        </Field>
        <Field label="Z m">
          <NumberInput value={plane.z} onChange={(z) => set({ z })} step={0.1} precision={3} />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Field label="Width m">
          <NumberInput
            value={plane.w}
            onChange={(w) => set({ w })}
            min={MIN_PLANE_SIZE}
            step={0.1}
            precision={2}
          />
        </Field>
        <Field label="Depth m">
          <NumberInput
            value={plane.d}
            onChange={(d) => set({ d })}
            min={MIN_PLANE_SIZE}
            step={0.1}
            precision={2}
          />
        </Field>
        <Field label="Rotation °">
          <NumberInput value={plane.rot} onChange={(rot) => set({ rot })} step={15} precision={1} unit="°" />
        </Field>
      </div>

      <Field label="Opacity %">
        <NumberInput
          value={plane.opacity}
          onChange={(opacity) => set({ opacity })}
          min={0}
          max={100}
          step={10}
          unit="%"
        />
      </Field>

      <Checkbox checked={plane.visible} onChange={(visible) => set({ visible })} label="Visible" />
      <Checkbox
        checked={plane.collide}
        onChange={(collide) => set({ collide })}
        label="Standards can stand on it"
        hint="a surface, not just a guide"
      />
    </div>
  );
}
